import { inject } from '@angular/core';

import { NgpDialogRef } from 'ng-primitives/dialog';

import {
  SuiDialogConfig,
  DIALOG_SIZES,
  DIALOG_VIEWPORT_LIMITS,
} from './dialog-config';
import { ZIndex } from '@sensika/sui/shared';

/**
 * Base class for dialog LAYOUT wrappers (not business components).
 *
 * Provides shared functionality for all dialog layout variants:
 * - Dimension/size handling (width, height, min/max)
 * - Z-index management for stacking
 * - Style computation for dialog and overlay
 *
 * @internal This is an implementation detail. Not exported from the module.
 *
 * Layout wrappers that extend this:
 * - SuiDialogTabsComponent (two-panel tabbed dialog)
 * - SuiDialogSimpleComponent (single-panel simple dialog)
 *
 * Note: This is different from SuiDialogBase which is for BUSINESS components.
 * - DialogLayoutBase → layout wrappers (slots, structure, styling)
 * - SuiDialogBase → business components (data, logic, close handling)
 */
export abstract class DialogLayoutBase {
  protected dialogRef = inject(NgpDialogRef);
  protected config = this.dialogRef.config as SuiDialogConfig;

  // * Z-index for dialog panel
  // TODO: this needs to be adapted to the z-index layers
  protected get dialogZIndex(): number {
    return this.config.zIndex || ZIndex.DIALOG;
  }

  // * Z-index for overlay (always below dialog)
  // TODO: this needs to be adapted to the z-index layers
  protected get overlayZIndex(): number {
    return this.config.zIndex ? this.config.zIndex - 1 : ZIndex.OVERLAY;
  }

  // * Custom inline styles for dimensions and z-index
  protected get customStyles(): Record<string, string> | null {
    const styles: Record<string, string> = {};
    let hasCustomStyles = false;

    // ! Apply z-index if provided (avoid manual setting of z-index)
    if (this.config.zIndex) {
      styles['z-index'] = this.config.zIndex.toString();
      hasCustomStyles = true;
    }

    // ! if predefined size is used, apply it via styles
    if (this.config.size && DIALOG_SIZES[this.config.size]) {
      const sizeConfig = DIALOG_SIZES[this.config.size];
      if (sizeConfig.width) {
        styles['width'] = sizeConfig.width;
        hasCustomStyles = true;
      }
      if (sizeConfig.height) {
        styles['height'] = sizeConfig.height;
        hasCustomStyles = true;
      }
      if (sizeConfig.minWidth) {
        styles['min-width'] = sizeConfig.minWidth;
        hasCustomStyles = true;
      }
      if (sizeConfig.minHeight) {
        styles['min-height'] = sizeConfig.minHeight;
        hasCustomStyles = true;
      }
    }

    // ! custom dimensions override predefined sizes.
    // ! Numeric width/height/min* get clamped to the viewport (see
    // ! resolveDimension); max* pass through — caps can't force overflow.
    if (this.config.width) {
      styles['width'] = this.resolveDimension(this.config.width, 'width');
      hasCustomStyles = true;
    }
    if (this.config.height) {
      styles['height'] = this.resolveDimension(this.config.height, 'height');
      hasCustomStyles = true;
    }
    if (this.config.maxWidth) {
      styles['max-width'] = this.resolveDimension(this.config.maxWidth);
      hasCustomStyles = true;
    }
    if (this.config.maxHeight) {
      styles['max-height'] = this.resolveDimension(this.config.maxHeight);
      hasCustomStyles = true;
    }
    if (this.config.minWidth) {
      styles['min-width'] = this.resolveDimension(
        this.config.minWidth,
        'width',
      );
      hasCustomStyles = true;
    }
    if (this.config.minHeight) {
      styles['min-height'] = this.resolveDimension(
        this.config.minHeight,
        'height',
      );
      hasCustomStyles = true;
    }

    return hasCustomStyles ? styles : null;
  }

  // * Overlay styles (just z-index)
  protected get overlayStyles(): Record<string, string> | null {
    if (!this.overlayZIndex) return null;
    return { 'z-index': this.overlayZIndex.toString() };
  }

  // * Numbers are protected, strings are raw (the developer is in charge to decide and no clamping is applied).
  // * NUmber:
  // * - 1536 + axis      -> min(1536px, 95vw) (collapses to 1536px when viewport is big enough)
  // * String:
  // * - '1536px', '50vw' -> passed through, no clamp
  // * `clampAxis` is omitted for max* — caps can't force overflow.
  protected resolveDimension(
    value: number | string,
    clampAxis?: 'width' | 'height',
  ): string {
    if (typeof value === 'string') {
      return value;
    }
    const px = `${value}px`;
    if (!clampAxis) {
      return px;
    }
    return `min(${px}, ${DIALOG_VIEWPORT_LIMITS[clampAxis]})`;
  }
}
